/**
 * Install copy that depends on Anthropic's product rather than ours: Claude app
 * menu paths, Claude Code CLI commands, which runtime supports which plug-in
 * component, and the Anthropic docs the site links to.
 *
 * Menu paths, commands, the client label, the Chat-unsupported components and
 * doc links live here, and every page renders them from here, so changing one
 * value updates every page that shows it. Other Anthropic-dependent wording
 * stays inline in components (e.g. "Individual skills aren't listed in
 * Organization Plugins"); those sentences are tracked as quoted `locations` in
 * the skill's sources.json instead. A fix for one claim can therefore touch
 * both: the value here and each location sources.json lists for that claim.
 *
 * The check-install-instructions skill
 * (.claude/skills/check-install-instructions/) compares this copy against
 * Anthropic's current docs, release notes and changelog. Its sources.json maps
 * each claim to an export below by name; install-instructions.test.ts fails if
 * the two fall out of sync and pins every value, so a change here updates the
 * pin and sources.json in the same PR.
 */

/** How the site names the Claude desktop/web app, as opposed to Claude Code. */
export const CLAUDE_APP_LABEL = "Claude (Chat & Cowork)";

/** Menu paths in the Claude app. Rendered inside <strong>. */
export const CLAUDE_APP_PATHS = {
  /** Where org-published plug-ins are listed. */
  organizationPlugins: "Customize → Organization Plugins",
  /** Entry point for browsing and installing an org plug-in. */
  installOrganizationPlugin: "Customize → Organization Plugins → + Icon",
  /** Manual upload of a downloaded .plugin file. */
  uploadPlugin: "Customize → Upload Plugin",
  /** Manual upload of a downloaded skill.md file. */
  uploadSkill: "Customize → Skills → Create skill → Upload a skill",
} as const;

/** Claude Code CLI commands, built from our marketplace values. */
export const CLAUDE_CODE_COMMANDS = {
  addMarketplace: (catalogUrl: string) => `claude plugin marketplace add ${catalogUrl}`,
  install: (plugin: string, marketplace: string) => `claude plugin install ${plugin}@${marketplace}`,
  installInSession: (plugin: string, marketplace: string) => `/plugin install ${plugin}@${marketplace}`,
  reloadInSession: "/reload-plugins",
  validate: "claude plugin validate",
} as const;

/**
 * Plug-in components that run in Claude Cowork and Claude Code but not in
 * Claude Chat. Keys match the MarketplaceEntry arrays the detail page checks
 * (`plugin.hooks`, `plugin.agents`); values are the display names.
 */
export const CHAT_UNSUPPORTED_COMPONENTS = {
  hooks: "hooks",
  agents: "sub-agents",
} as const;

/** Every Chat-unsupported component in one phrase, e.g. "hooks and sub-agents". */
export const CHAT_UNSUPPORTED_COMPONENTS_TEXT = new Intl.ListFormat("en", { type: "conjunction" })
  .format(Object.values(CHAT_UNSUPPORTED_COMPONENTS));

/** Anthropic docs the site links to. */
export const ANTHROPIC_DOCS = {
  usePluginsInClaude: "https://support.claude.com/en/articles/13837440-use-plugins-in-claude",
  browsePlugins: "https://claude.com/plugins",
  claudeCodeDiscoverPlugins: "https://code.claude.com/docs/en/discover-plugins",
  claudeCodePlugins: "https://code.claude.com/docs/en/plugins",
  claudeCodeCliReference: "https://code.claude.com/docs/en/cli-reference",
} as const;
